/* ==========================================================================
   FITLIX — global.js
   Vanilla JS, no jQuery, no external deps. Loaded with `defer`.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Utilities
  ------------------------------------------------------------------ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts || false); }

  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  /* ------------------------------------------------------------------
     Image fade-in-on-load — single choke point reused for every <img> in
     the theme (responsive-image.liquid output, hand-rolled PDP gallery
     markup, and anything injected later by cart/search/wishlist JS).
  ------------------------------------------------------------------ */
  function fadeInImage(img) {
    if (!img || img.classList.contains('is-img-loaded') || img.classList.contains('is-img-loading')) return;
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('is-img-loaded');
      return;
    }
    img.classList.add('is-img-loading');
    on(img, 'load', function () {
      img.classList.remove('is-img-loading');
      img.classList.add('is-img-loaded');
    }, { once: true });
    on(img, 'error', function () {
      img.classList.remove('is-img-loading');
    }, { once: true });
  }

  function initImageFade() {
    qsa('img').forEach(fadeInImage);
  }

  /* ------------------------------------------------------------------
     Sticky header + transparent-over-hero behaviour
  ------------------------------------------------------------------ */
  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;

    var isTransparent = header.getAttribute('data-transparent') === 'true';
    var threshold = 40;
    var hideThreshold = header.offsetHeight + 40;
    var lastY = window.scrollY;
    var ticking = false;

    function handleScroll() {
      var y = window.scrollY;
      var scrolled = y > threshold;
      header.classList.toggle('is-stuck', scrolled && !isTransparent);
      if (isTransparent) header.classList.toggle('is-scrolled', scrolled);

      // Hide the header on downward scroll past its own height, reveal it on
      // any upward scroll. Suspended while a drawer is open or while focus is
      // inside the header (keyboard mega-menu nav), so it never disappears
      // mid-interaction.
      var delta = y - lastY;
      var interacting = document.body.style.overflow === 'hidden' || header.contains(document.activeElement);
      if (!interacting) {
        if (y > hideThreshold && delta > 4) {
          header.classList.add('is-header-hidden');
        } else if (delta < -4 || y <= hideThreshold) {
          header.classList.remove('is-header-hidden');
        }
      }
      lastY = y;
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    }

    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', function () { hideThreshold = header.offsetHeight + 40; }, { passive: true });
    handleScroll();
  }

  /* ------------------------------------------------------------------
     Mobile drawer
  ------------------------------------------------------------------ */
  function initMobileDrawer() {
    var drawer = qs('#fx-mobile-drawer');
    var openBtn = qs('[data-drawer-open]');
    if (!drawer) return;

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var closeBtn = qs('[data-drawer-close]', drawer);
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    }

    on(openBtn, 'click', open);
    qsa('[data-drawer-close]', drawer).forEach(function (btn) {
      on(btn, 'click', close);
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close();
    });

    // Menu links live inside <summary> (so the row also toggles the accordion) —
    // stop the click from bubbling to <summary>'s native toggle so tapping the
    // link text navigates instead of just expanding/collapsing the group.
    qsa('[data-drawer-navlink]', drawer).forEach(function (link) {
      on(link, 'click', function (e) { e.stopPropagation(); });
    });
  }

  /* ------------------------------------------------------------------
     Search drawer - product discovery

     Shopify's own predictive search already resolves typos, partial words
     and brand spellings for this catalogue. That was measured against 38 real
     queries before any of this was written: "muscelblaze", "whey protien",
     "creatinee", "avatar whey" and "optium nutrtion whey" all return the right
     products with no help from us. So there is no fuzzy matcher here and no
     synonym table. Replacing a working engine with a hand-rolled one would be
     a downgrade.

     What the old code did wrong was throw away two thirds of the answer. The
     same endpoint returns `collections` and `queries` alongside `products`;
     it only ever asked for products. Brands and categories below are derived
     from that one response, so the richer result costs no extra requests.
  ------------------------------------------------------------------ */
  function initSearchDrawer() {
    var drawer = qs('#fx-search-drawer');
    if (!drawer) return;

    var openBtn   = qs('[data-search-open]');
    var input     = qs('#fx-predictive-search-input');
    var resultsEl = qs('#fx-predictive-search-results');
    var clearBtn  = qs('[data-search-clear]', drawer);
    var statusEl  = qs('[data-search-status]', drawer);
    var form      = qs('.fx-search-drawer__form', drawer);

    var DEBOUNCE   = 180;
    var MIN_CHARS  = 1;
    var RECENT_KEY = 'fitlix_recent_searches';
    var RECENT_MAX = 5;

    var data = {};
    try {
      var tag = qs('#fx-search-data');
      if (tag) data = JSON.parse(tag.textContent) || {};
    } catch (e) { data = {}; }
    var collections = data.collections || [];
    var browse      = data.browse || [];
    var searchUrl   = data.searchUrl || '/search';

    var cache       = {};      // query -> parsed response, per page load
    var controller  = null;    // in-flight request, so a slow early reply
    var debounceId  = null;    // can never overwrite a newer one
    var lastFocused = null;
    var items       = [];      // keyboard-navigable anchors, in visual order

    /* ---------- recent searches (this browser only) ---------- */
    function recentGet() {
      try {
        var v = JSON.parse(localStorage.getItem(RECENT_KEY));
        return Array.isArray(v) ? v.slice(0, RECENT_MAX) : [];
      } catch (e) { return []; }
    }
    function recentAdd(q) {
      if (!q) return;
      try {
        var list = recentGet().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
        list.unshift(q);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) { /* private mode, nothing to do */ }
    }
    function recentClear() {
      try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* noop */ }
      renderResting();
    }

    /* ---------- tiny DOM helpers, so nothing is built by string ---------- */
    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }
    function group(title) {
      var sec = el('div', 'fx-sd__group');
      if (title) sec.appendChild(el('p', 'fx-sd__grouptitle', title));
      return sec;
    }
    function money(cents) {
      var n = parseFloat(cents);
      if (isNaN(n)) return '';
      return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    /* ---------- resting state: nothing typed yet ---------- */
    function renderResting() {
      resultsEl.innerHTML = '';
      items = [];
      input.setAttribute('aria-expanded', 'false');

      var recent = recentGet();
      if (recent.length) {
        var g = group(null);
        var head = el('div', 'fx-sd__grouphead');
        head.appendChild(el('p', 'fx-sd__grouptitle', 'Recent'));
        var clr = el('button', 'fx-sd__clearrecent', 'Clear');
        clr.type = 'button';
        on(clr, 'click', recentClear);
        head.appendChild(clr);
        g.appendChild(head);

        var chips = el('div', 'fx-sd__chips');
        recent.forEach(function (q) {
          var a = el('a', 'fx-sd__chip', q);
          a.href = searchUrl + '?q=' + encodeURIComponent(q) + '&type=product';
          chips.appendChild(a);
          items.push(a);
        });
        g.appendChild(chips);
        resultsEl.appendChild(g);
      }

      if (browse.length) {
        var gb = group('Browse');
        var cb = el('div', 'fx-sd__chips');
        browse.forEach(function (b) {
          var a = el('a', 'fx-sd__chip', b.t);
          a.href = b.u;
          cb.appendChild(a);
          items.push(a);
        });
        gb.appendChild(cb);
        resultsEl.appendChild(gb);
      }
    }

    /* ---------- loading: skeleton shaped like the real rows ---------- */
    function renderLoading() {
      resultsEl.innerHTML = '';
      items = [];
      var g = group(null);
      for (var i = 0; i < 3; i++) {
        var row = el('div', 'fx-sd__skel');
        row.appendChild(el('span', 'fx-sd__skel-img'));
        var lines = el('span', 'fx-sd__skel-lines');
        lines.appendChild(el('span', 'fx-sd__skel-line'));
        lines.appendChild(el('span', 'fx-sd__skel-line is-short'));
        row.appendChild(lines);
        g.appendChild(row);
      }
      resultsEl.appendChild(g);
    }

    /* ---------- one product row ---------- */
    function productRow(p) {
      var a = el('a', 'fx-sd__product');
      a.href = p.url;
      if (!p.available) a.className += ' is-unavailable';

      var media = el('span', 'fx-sd__media');
      if (p.featured_image && p.featured_image.url) {
        var img = document.createElement('img');
        img.src = p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
        img.alt = '';
        img.width = 56; img.height = 56; img.loading = 'lazy'; img.decoding = 'async';
        media.appendChild(img);
      } else if (p.image) {
        var im2 = document.createElement('img');
        im2.src = p.image; im2.alt = ''; im2.width = 56; im2.height = 56;
        im2.loading = 'lazy'; im2.decoding = 'async';
        media.appendChild(im2);
      }
      a.appendChild(media);

      var body = el('span', 'fx-sd__body');
      if (p.vendor) body.appendChild(el('span', 'fx-sd__vendor', p.vendor));
      body.appendChild(el('span', 'fx-sd__title', p.title));

      var meta = el('span', 'fx-sd__meta');
      if (p.available) {
        meta.appendChild(el('span', 'fx-sd__price', money(p.price)));
        var cmp = parseFloat(p.compare_at_price_max);
        var now = parseFloat(p.price);
        if (cmp && now && cmp > now) {
          meta.appendChild(el('span', 'fx-sd__was', money(cmp)));
          meta.appendChild(el('span', 'fx-sd__off', Math.round((cmp - now) / cmp * 100) + '% off'));
        }
      } else {
        meta.appendChild(el('span', 'fx-sd__sold', 'Sold out'));
      }
      body.appendChild(meta);
      a.appendChild(body);
      return a;
    }

    /* ---------- vendor -> real collection where one exists ---------- */
    function brandHref(vendor) {
      var v = vendor.toLowerCase().replace(/[^a-z0-9]/g, '');
      for (var i = 0; i < collections.length; i++) {
        if (collections[i].t.toLowerCase().replace(/[^a-z0-9]/g, '') === v) {
          return '/collections/' + collections[i].h;
        }
      }
      return searchUrl + '?q=' + encodeURIComponent(vendor) + '&type=product';
    }

    /* ---------- results ---------- */
    function render(res, query) {
      var products    = (res.products || []);
      var colls       = (res.collections || []);
      var suggestions = (res.queries || []);

      resultsEl.innerHTML = '';
      items = [];

      // Available first. Shopify is asked to do this too, but a card that is
      // purchasable should never sit under one that is not.
      products = products.slice().sort(function (a, b) {
        return (b.available ? 1 : 0) - (a.available ? 1 : 0);
      });

      if (!products.length && !colls.length && !suggestions.length) {
        renderEmpty(query);
        return;
      }

      // Suggestions: Shopify's own query completions, minus the one already typed.
      var sugg = suggestions.filter(function (s) {
        return s.text && s.text.toLowerCase() !== query.toLowerCase();
      }).slice(0, 3);
      if (sugg.length) {
        var gs = group('Suggestions');
        sugg.forEach(function (s) {
          var a = el('a', 'fx-sd__suggest');
          a.href = searchUrl + '?q=' + encodeURIComponent(s.text) + '&type=product';
          a.appendChild(el('span', 'fx-sd__suggest-icon'));
          a.appendChild(el('span', null, s.text));
          gs.appendChild(a);
          items.push(a);
        });
        resultsEl.appendChild(gs);
      }

      if (products.length) {
        var gp = group('Products');
        products.slice(0, 6).forEach(function (p) {
          var row = productRow(p);
          gp.appendChild(row);
          items.push(row);
        });
        resultsEl.appendChild(gp);
      }

      // Brands and categories, derived from the products already in hand.
      var seenV = {}, vendors = [];
      products.forEach(function (p) {
        if (p.vendor && !seenV[p.vendor]) { seenV[p.vendor] = 1; vendors.push(p.vendor); }
      });
      if (vendors.length) {
        var gb = group('Brands');
        var cb = el('div', 'fx-sd__chips');
        vendors.slice(0, 5).forEach(function (v) {
          var a = el('a', 'fx-sd__chip', v);
          a.href = brandHref(v);
          cb.appendChild(a);
          items.push(a);
        });
        gb.appendChild(cb);
        resultsEl.appendChild(gb);
      }

      var seenC = {}, cats = [];
      colls.forEach(function (c) {
        if (!seenC[c.title]) { seenC[c.title] = 1; cats.push({ t: c.title, u: c.url }); }
      });
      products.forEach(function (p) {
        if (p.type && !seenC[p.type]) {
          seenC[p.type] = 1;
          cats.push({ t: p.type, u: searchUrl + '?q=' + encodeURIComponent(p.type) + '&type=product' });
        }
      });
      if (cats.length) {
        var gc = group('Categories');
        var cc = el('div', 'fx-sd__chips');
        cats.slice(0, 5).forEach(function (c) {
          var a = el('a', 'fx-sd__chip', c.t);
          a.href = c.u;
          cc.appendChild(a);
          items.push(a);
        });
        gc.appendChild(cc);
        resultsEl.appendChild(gc);
      }

      var all = el('a', 'fx-sd__all');
      all.href = searchUrl + '?q=' + encodeURIComponent(query) + '&type=product';
      all.appendChild(el('span', null, 'View all results for “' + query + '”'));
      all.appendChild(el('span', 'fx-sd__all-arrow', '→'));
      resultsEl.appendChild(all);
      items.push(all);

      input.setAttribute('aria-expanded', 'true');
      if (statusEl) {
        statusEl.textContent = products.length
          ? products.length + ' product suggestions for ' + query
          : 'No products for ' + query + '. Showing related categories.';
      }
    }

    /* ---------- nothing matched: recover, never dead-end ---------- */
    function renderEmpty(query) {
      resultsEl.innerHTML = '';
      items = [];
      var g = group(null);
      g.appendChild(el('p', 'fx-sd__emptytitle', 'No match for “' + query + '”'));
      g.appendChild(el('p', 'fx-sd__emptybody', 'Check the spelling, or start from a category below.'));
      resultsEl.appendChild(g);

      if (browse.length) {
        var gb = group('Browse');
        var cb = el('div', 'fx-sd__chips');
        browse.forEach(function (b) {
          var a = el('a', 'fx-sd__chip', b.t);
          a.href = b.u;
          cb.appendChild(a);
          items.push(a);
        });
        gb.appendChild(cb);
        resultsEl.appendChild(gb);
      }
      input.setAttribute('aria-expanded', 'false');
      if (statusEl) statusEl.textContent = 'No results for ' + query;
    }

    function renderError() {
      resultsEl.innerHTML = '';
      items = [];
      var g = group(null);
      g.appendChild(el('p', 'fx-sd__emptytitle', 'Search is temporarily unavailable'));
      g.appendChild(el('p', 'fx-sd__emptybody', 'Please try again in a moment.'));
      resultsEl.appendChild(g);
    }

    /* ---------- fetch ---------- */
    function run(query) {
      var key = query.toLowerCase();
      if (cache[key]) { render(cache[key], query); return; }

      if (controller) { try { controller.abort(); } catch (e) { /* noop */ } }
      controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      renderLoading();

      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(query)
        + '&resources[type]=product,collection,query'
        + '&resources[limit]=6'
        + '&resources[options][unavailable_products]=last';

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) {
          if (!r.ok) throw new Error('bad status');
          return r.json();
        })
        .then(function (json) {
          var res = (json.resources && json.resources.results) || {};
          cache[key] = res;
          if (input.value.trim() === query) render(res, query);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;   // superseded, not a failure
          renderError();
        });
    }

    /* ---------- open / close ---------- */
    function open() {
      lastFocused = document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      renderResting();
      if (input) {
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }
      document.addEventListener('keydown', onTrap);
    }
    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onTrap);
      clearTimeout(debounceId);
      if (controller) { try { controller.abort(); } catch (e) { /* noop */ } }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
      }
    }

    var FOCUSABLE = 'a[href], button, input, [tabindex]:not([tabindex="-1"])';
    function onTrap(e) {
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null && !n.disabled && !n.hidden; }
      );
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }

    on(openBtn, 'click', open);
    qsa('[data-search-close]', drawer).forEach(function (btn) { on(btn, 'click', close); });

    /* ---------- typing ---------- */
    on(input, 'input', function () {
      var query = input.value.replace(/\s+/g, ' ').trim();
      if (clearBtn) clearBtn.hidden = !input.value;
      clearTimeout(debounceId);

      if (query.length < MIN_CHARS) {
        if (controller) { try { controller.abort(); } catch (e) { /* noop */ } }
        renderResting();
        return;
      }
      debounceId = setTimeout(function () { run(query); }, DEBOUNCE);
    });

    on(clearBtn, 'click', function () {
      input.value = '';
      clearBtn.hidden = true;
      renderResting();
      input.focus();
    });

    on(form, 'submit', function () { recentAdd(input.value.replace(/\s+/g, ' ').trim()); });
    on(resultsEl, 'click', function (e) {
      if (e.target.closest && e.target.closest('a')) {
        recentAdd(input.value.replace(/\s+/g, ' ').trim());
      }
    });

    /* ---------- keyboard ---------- */
    on(drawer, 'keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!items.length) return;
      e.preventDefault();
      var i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') i = (i < 0) ? 0 : Math.min(i + 1, items.length - 1);
      else i = (i <= 0) ? -1 : i - 1;
      if (i < 0) { input.focus(); return; }
      items[i].focus();
    });
  }

  /* ------------------------------------------------------------------
     Mega menu keyboard nav
  ------------------------------------------------------------------ */
  function initMegaMenu() {
    qsa('.fx-header__menu-item.has-dropdown').forEach(function (item) {
      var link = qs('a', item);
      on(item, 'keydown', function (e) {
        if (e.key === 'Escape') {
          if (link) link.focus();
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Cart drawer + AJAX add-to-cart
  ------------------------------------------------------------------ */
  // Binders for product-card controls, run again on cards added after load.
  var cardBinders = [];
  function bindCards(root) {
    cardBinders.forEach(function (fn) { fn(root); });
    qsa('img', root).forEach(fadeInImage);
    qsa('.fx-animate', root).forEach(function (el) { el.classList.add('is-visible'); });
  }

  function initCart() {
    var drawer = qs('#fx-cart-drawer');
    var openBtn = qs('[data-cart-open]');

    function open() {
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }
    }
    function close() {
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }

    on(openBtn, 'click', open);
    if (drawer) {
      qsa('[data-cart-close]', drawer).forEach(function (btn) { on(btn, 'click', close); });
    }
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.getAttribute('aria-hidden') === 'false') close();
    });

    // Product-card Add to cart / Buy now. Handled by ONE delegated listener on
    // the document instead of wiring each button: cards that arrive later (the
    // "Picked for you" row, infinite scroll, any app or section that injects
    // cards) work automatically. Per-button wiring is what left those dead.
    on(document, 'submit', function (e) {
      var form = e.target.closest && e.target.closest('form[data-quick-add-form]');
      if (!form) return;
      e.preventDefault();
      var btn = qs('button[type="submit"]', form);
      var id = new FormData(form).get('id');
      if (!id) return;
      addOne(id, btn);
    }, true);

    on(document, 'click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var el = e.target.closest && e.target.closest('[data-buy-now], [data-add-choose]');
      if (!el) return;
      e.preventDefault();
      if (el.disabled || el.classList.contains('is-adding')) return;
      if (el.hasAttribute('data-buy-now-choose')) { openVariantChooser(el, 'buy'); return; }
      if (el.hasAttribute('data-add-choose')) { openVariantChooser(el, 'cart'); return; }
      var id = el.getAttribute('data-variant-id');
      if (id) buyNow(id, el);
    }, true);

    // POST one unit; resolves only when Shopify really accepted the line.
    function addStrict(id) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 })
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'This item could not be added.');
          return data;
        });
      });
    }

    function busy(btn, on_) {
      if (!btn) return;
      btn.classList.toggle('is-adding', on_);
      if (btn.tagName === 'BUTTON') btn.disabled = on_;
    }

    // Add to cart: add, refresh the drawer, open it.
    // The product page's own Add to cart (fitlix-product.js) fires
    // 'product:added'; open the drawer there too, so the shopper sees the cart
    // and the Checkout button instead of only a brief "Added" label.
    on(document, 'product:added', function () { open(); });

    function addOne(id, btn) {
      busy(btn, true);
      addStrict(id)
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          busy(btn, false);
          dispatchCartUpdated(cart);
          if (chooser && chooser.open) chooser.close();
          open();
        })
        .catch(function (err) {
          busy(btn, false);
          buyNowMessage(btn, err && err.message);
        });
    }

    // Buy now: add, then checkout - only if Shopify accepted the line.
    // /checkout with an empty cart redirects to the homepage, which is where
    // customers landed whenever an add was rejected.
    function buyNow(id, btn) {
      busy(btn, true);
      addStrict(id)
        .then(function () { window.location.href = '/checkout'; })
        .catch(function (err) {
          busy(btn, false);
          buyNowMessage(btn, err && err.message);
        });
    }

    function buyNowMessage(btn, message) {
      var host = (btn && btn.closest('.fx-product-card__actions, .fx-buynow-dialog__panel')) || (btn && btn.parentNode) || document.body;
      var old = qs('.fx-buynow-note', host);
      if (old) old.remove();
      var note = document.createElement('p');
      note.className = 'fx-buynow-note';
      note.setAttribute('role', 'alert');
      note.textContent = message || 'Something went wrong. Please try again.';
      host.appendChild(note);
      setTimeout(function () { note.remove(); }, 5000);
    }

    // One shared flavour/size chooser for multi-variant cards. Variant data
    // comes from the card (data-variants), so the id sent is the one picked.
    var chooser = null;
    var chooserMode = 'buy';
    function openVariantChooser(btn, mode) {
      var variants;
      try { variants = JSON.parse(btn.getAttribute('data-variants') || '[]'); } catch (err) { variants = []; }
      if (!variants.length || typeof HTMLDialogElement === 'undefined') {
        window.location.href = btn.getAttribute('href');
        return;
      }
      chooserMode = mode;
      if (!chooser) {
        chooser = document.createElement('dialog');
        chooser.className = 'fx-buynow-dialog';
        chooser.innerHTML =
          '<form method="dialog" class="fx-buynow-dialog__panel">' +
            '<button type="submit" class="fx-buynow-dialog__close" value="cancel" aria-label="Close">×</button>' +
            '<p class="fx-buynow-dialog__eyebrow">Choose an option</p>' +
            '<p class="fx-buynow-dialog__title"></p>' +
            '<div class="fx-buynow-dialog__options" role="radiogroup"></div>' +
            '<button type="button" class="fx-btn fx-btn--primary fx-btn--block fx-buynow-dialog__go">Buy now</button>' +
            '<a class="fx-buynow-dialog__details" href="#">View full product details</a>' +
          '</form>';
        document.body.appendChild(chooser);
        on(chooser, 'click', function (e) { if (e.target === chooser) chooser.close(); });
        on(chooser, 'change', updateGo);
        on(qs('.fx-buynow-dialog__go', chooser), 'click', function () {
          var picked = qs('input[name="fx-buynow-variant"]:checked', chooser);
          if (!picked) return;
          if (chooserMode === 'cart') addOne(picked.value, this);
          else buyNow(picked.value, this);
        });
      }
      qs('.fx-buynow-dialog__title', chooser).textContent = btn.getAttribute('data-product-title') || '';
      qs('.fx-buynow-dialog__details', chooser).setAttribute('href', btn.getAttribute('href'));
      var list = qs('.fx-buynow-dialog__options', chooser);
      list.innerHTML = '';
      var firstAvailable = null;
      variants.forEach(function (v, i) {
        var label = document.createElement('label');
        label.className = 'fx-buynow-dialog__option' + (v.available ? '' : ' is-soldout');
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'fx-buynow-variant';
        input.value = v.id;
        input.disabled = !v.available;
        input.setAttribute('data-price', v.price);
        if (v.available && firstAvailable === null) { firstAvailable = i; input.checked = true; }
        var name = document.createElement('span');
        name.textContent = v.title + (v.available ? '' : ' — sold out');
        var price = document.createElement('span');
        price.className = 'fx-buynow-dialog__price';
        price.textContent = formatMoney(v.price);
        label.appendChild(input);
        label.appendChild(name);
        label.appendChild(price);
        list.appendChild(label);
      });
      var go = qs('.fx-buynow-dialog__go', chooser);
      go.disabled = firstAvailable === null;
      go.classList.remove('is-adding');
      var stale = qs('.fx-buynow-note', chooser);
      if (stale) stale.remove();
      updateGo();
      chooser.showModal();
    }
    function updateGo() {
      var picked = qs('input[name="fx-buynow-variant"]:checked', chooser);
      var go = qs('.fx-buynow-dialog__go', chooser);
      var label = chooserMode === 'cart' ? 'Add to cart' : 'Buy now';
      go.textContent = picked ? label + ' — ' + formatMoney(parseInt(picked.getAttribute('data-price'), 10)) : 'Sold out';
    }

    // Coming back from checkout with the Back button restores the page from
    // cache with buttons still disabled; release them.
    on(window, 'pageshow', function (e) {
      if (!e.persisted) return;
      qsa('.is-adding').forEach(function (b) { busy(b, false); });
      if (chooser && chooser.open) chooser.close();
    });

    // "Add to Stack" forms — same add-to-cart flow, tagged with a line item property
    qsa('[data-stack-add-form]').forEach(function (form) {
      on(form, 'submit', function (e) {
        e.preventDefault();
        var formData = new FormData(form);
        var body = { id: formData.get('id'), quantity: 1, properties: {} };
        formData.forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (match) body.properties[match[1]] = value;
        });
        var btn = qs('.fx-product-card__stackadd', form);
        if (btn) { btn.classList.add('is-adding'); btn.disabled = true; }
        addToCart(body).then(function (cart) {
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('is-adding');
            if (cart) {
              btn.classList.add('is-added');
              setTimeout(function () { btn.classList.remove('is-added'); }, 1200);
            }
          }
          if (cart) open();
        });
      });
    });

    // Stack "add all" buttons
    qsa('[data-stack-add]').forEach(function (btn) {
      on(btn, 'click', function () {
        var ids = (btn.getAttribute('data-variant-ids') || '')
          .split(',')
          .map(function (id) { return id.trim(); })
          .filter(Boolean);
        if (!ids.length) return;
        var items = ids.map(function (id) { return { id: id, quantity: 1 }; });
        addMultipleToCart(items).then(function () {
          open();
        });
      });
    });

    on(document, 'cart:updated', function (e) {
      renderCartDrawer(e.detail && e.detail.cart);
      updateCartCount(e.detail && e.detail.cart);
    });

    // Quantity +/-, remove — event-delegated since lines are re-rendered on every update
    var itemsEl = qs('#fx-cart-drawer-items');
    on(itemsEl, 'click', function (e) {
      var line = e.target.closest('.fx-cart-line');
      if (!line || line.classList.contains('is-updating') || line.classList.contains('is-removing')) return;
      var key = line.getAttribute('data-line-key');
      if (!key) return;

      var qtyEl = qs('.fx-cart-line__qty-value', line);
      var currentQty = qtyEl ? (parseInt(qtyEl.textContent, 10) || 1) : 1;
      var nextQty = null;

      if (e.target.closest('[data-qty-increase]')) {
        nextQty = currentQty + 1;
      } else if (e.target.closest('[data-qty-decrease]')) {
        nextQty = Math.max(0, currentQty - 1);
      } else if (e.target.closest('.fx-cart-line__remove')) {
        nextQty = 0;
      }

      if (nextQty === null) return;

      if (nextQty === 0) {
        // Send the removal now. It used to wait for the fold animation's
        // transitionend, but a re-render from another line's response replaced
        // this node first, so the event never fired and the removal was lost.
        // pendingRemovals keeps the line folded through any re-render until its
        // own request settles; the render waits for the fold to finish.
        pendingRemovals[key] = true;
        line.classList.add('is-removing');
        queueCartChange(key, 0, foldDuration(line));
        return;
      }

      // Optimistic: show the new number and pulse it immediately rather than
      // waiting on the round trip, then let the eventual rerender confirm it.
      if (qtyEl) {
        qtyEl.textContent = nextQty;
        qtyEl.classList.remove('is-pulsing');
        void qtyEl.offsetWidth;
        qtyEl.classList.add('is-pulsing');
      }
      line.classList.add('is-updating');
      queueCartChange(key, nextQty, 0);
    });

    // Populate drawer with whatever is already in the cart on page load
    // (it otherwise only renders reactively after an add/change on this page).
    fetchCart().then(function (cart) {
      renderCartDrawer(cart);
      updateCartCount(cart);
    });
  }

  // Cart line changes run one at a time, in click order, so a slower earlier
  // response can never re-render over a newer cart.
  var cartQueue = Promise.resolve();
  var pendingRemovals = {};
  var latestCart = null;

  // Shopify can re-issue a line's key after any cart change (the hash part
  // follows the discounts applied), so a key read from the DOM may be stale by
  // the time its request runs. Match it against the latest cart: exact key
  // first, else the single line with the same variant.
  function sameLine(key, item, items) {
    if (item.key === key) return true;
    var variant = String(key).split(':')[0];
    if (String(item.variant_id) !== variant) return false;
    if (items.some(function (i) { return i.key === key; })) return false;
    return items.filter(function (i) { return String(i.variant_id) === variant; }).length === 1;
  }
  function resolveLineKey(key) {
    var items = (latestCart && latestCart.items) || [];
    for (var i = 0; i < items.length; i++) if (sameLine(key, items[i], items)) return items[i].key;
    return key;
  }
  function isPendingRemoval(item, items) {
    return Object.keys(pendingRemovals).some(function (k) { return sameLine(k, item, items); });
  }

  function foldDuration(el) {
    var d = (getComputedStyle(el).transitionDuration || '0s').split(',').map(function (s) {
      return s.indexOf('ms') > -1 ? parseFloat(s) : parseFloat(s) * 1000;
    });
    return Math.min(Math.max.apply(null, d.concat(0)) || 0, 800);
  }

  function queueCartChange(key, quantity, minDelay) {
    cartQueue = cartQueue.then(function () {
      var wait = new Promise(function (r) { setTimeout(r, minDelay || 0); });
      return Promise.all([changeCartLine(resolveLineKey(key), quantity, true), wait]).then(function (res) {
        delete pendingRemovals[key];
        if (res[0]) dispatchCartUpdated(res[0]);
        else fetchCart().then(dispatchCartUpdated);
      });
    }).catch(function () { delete pendingRemovals[key]; });
    return cartQueue;
  }

  function changeCartLine(key, quantity, deferRender) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) {
          // Server rejected the change (e.g. requested more than available stock) —
          // the response is an error payload, not a cart. Resync with the real cart
          // instead of trying to render the error as one.
          var message = (cart && (cart.description || cart.message)) || 'Could not update this item.';
          return fetchCart().then(function (freshCart) {
            delete pendingRemovals[key];
            dispatchCartUpdated(freshCart);
            showCartLineNote(key, message);
          });
        }
        if (!deferRender) dispatchCartUpdated(cart);
        return cart;
      })
      .catch(function (err) {
        console.error('Cart change failed', err);
        delete pendingRemovals[key];
        if (!deferRender) fetchCart().then(dispatchCartUpdated);
      });
  }

  function showCartLineNote(key, message) {
    var line = qs('.fx-cart-line[data-line-key="' + key + '"]');
    var info = line && qs('.fx-cart-line__info', line);
    if (!info) return;
    var note = document.createElement('p');
    note.className = 'fx-cart-line__note';
    note.textContent = message;
    info.appendChild(note);
    setTimeout(function () { note.remove(); }, 4000);
  }

  function addToCart(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(item)
    })
      .then(function (res) { return res.json(); })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        dispatchCartUpdated(cart);
        return cart;
      })
      .catch(function (err) { console.error('Add to cart failed', err); });
  }

  function addMultipleToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { return res.json(); })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        dispatchCartUpdated(cart);
        return cart;
      })
      .catch(function (err) { console.error('Add multiple to cart failed', err); });
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (res) { return res.json(); });
  }

  function updateCartCount(cart) {
    if (!cart) return;
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.setAttribute('data-cart-count', cart.item_count);
    });
  }

  function renderCartDrawer(cart) {
    if (!cart) return;
    if (cart.items) latestCart = cart;
    var itemsEl = qs('#fx-cart-drawer-items');
    var subtotalEl = qs('#fx-cart-drawer-subtotal');
    var template = qs('#fx-cart-line-item-template');
    if (!itemsEl) return;

    if (!cart.items.length) {
      itemsEl.innerHTML = '<p class="fx-cart-drawer__empty">Your cart is empty.</p>';
    } else if (template && template.content) {
      itemsEl.innerHTML = '';
      cart.items.forEach(function (item) {
        var node = template.content.cloneNode(true);
        var line = node.querySelector('.fx-cart-line');
        line.setAttribute('data-line-key', item.key);
        // A removal still in flight stays folded through intermediate re-renders.
        if (isPendingRemoval(item, cart.items)) line.classList.add('is-removing');

        var img = node.querySelector('.fx-cart-line__image');
        if (img) {
          if (item.image) {
            img.src = item.image;
            img.alt = item.product_title || '';
            fadeInImage(img);
          } else {
            // No product image: swap in the theme's standard placeholder box rather than
            // leaving an <img> with no src, which renders as a broken-image glyph.
            var placeholder = document.createElement('div');
            placeholder.className = 'fx-cart-line__image fx-image-placeholder';
            img.replaceWith(placeholder);
          }
        }

        var title = node.querySelector('.fx-cart-line__title');
        if (title) title.textContent = item.product_title;

        // Show real variant choices (flavour/size); never Shopify's "Default Title".
        var variant = node.querySelector('.fx-cart-line__variant');
        if (variant && item.variant_title && !item.product_has_only_default_variant && item.variant_title !== 'Default Title') {
          variant.textContent = item.variant_title;
          variant.hidden = false;
        }

        var price = node.querySelector('.fx-cart-line__price');
        if (price) {
          price.textContent = formatMoney(item.final_price) + ' × ' + item.quantity;
          if (item.original_price > item.final_price) {
            var was = document.createElement('s');
            was.textContent = formatMoney(item.original_price);
            price.insertBefore(was, price.firstChild);
          }
        }

        var qtyValue = node.querySelector('.fx-cart-line__qty-value');
        if (qtyValue) qtyValue.textContent = item.quantity;

        itemsEl.appendChild(node);
      });
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    renderCartDiscounts(cart);
  }

  // Aggregates every discount Shopify applied (line-level and cart-level, codes and
  // automatic) by title, so a lower subtotal is always explained to the customer.
  function renderCartDiscounts(cart) {
    var wrap = qs('#fx-cart-drawer-discounts');
    if (!wrap) return;
    var totals = {};
    var order = [];
    function add(title, amount) {
      if (!amount) return;
      if (!(title in totals)) { totals[title] = 0; order.push(title); }
      totals[title] += amount;
    }
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        add((a.discount_application && a.discount_application.title) || 'Discount', a.amount);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      add(d.title || 'Discount', d.total_allocated_amount);
    });
    var list = qs('[data-fx-cart-discount-list]', wrap);
    var original = qs('[data-fx-cart-original]', wrap);
    if (!order.length || !list) { wrap.hidden = true; return; }
    if (original) original.textContent = formatMoney(cart.original_total_price);
    list.innerHTML = '';
    order.forEach(function (t) {
      var li = document.createElement('li');
      li.className = 'fx-cart-summary-discounts__row fx-cart-summary-discounts__row--discount';
      var name = document.createElement('span');
      name.textContent = t;
      var amt = document.createElement('span');
      amt.textContent = '\u2212' + formatMoney(totals[t]);
      li.appendChild(name);
      li.appendChild(amt);
      list.appendChild(li);
    });
    wrap.hidden = false;
  }

  function formatMoney(cents) {
    // Thousands separators with Indian grouping (₹3,298.00, ₹1,24,990.00).
    return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ------------------------------------------------------------------
     Featured product filter tabs (tag-based)
  ------------------------------------------------------------------ */
  function initProductTabs() {
    qsa('[data-product-tabs]').forEach(function (tabWrap) {
      var section = tabWrap.closest('section');
      var grids = section ? qsa('[data-tab-content]', section) : [];
      if (!grids.length) return;
      var tabs = qsa('[data-tab]', tabWrap);

      function setActive(tabName) {
        tabs.forEach(function (t) {
          var active = t.getAttribute('data-tab') === tabName;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        grids.forEach(function (grid) {
          grid.style.display = grid.getAttribute('data-tab-content') === tabName ? '' : 'none';
        });
      }

      tabs.forEach(function (tab) {
        on(tab, 'click', function () { setActive(tab.getAttribute('data-tab')); });
      });

      if (tabs.length) setActive(tabs[0].getAttribute('data-tab'));
    });
  }

  /* ------------------------------------------------------------------
     Experience-level tabs (Beginner / Intermediate / Advanced)
  ------------------------------------------------------------------ */
  function initExperienceTabs() {
    qsa('[data-exp-tabs]').forEach(function (tabWrap) {
      var section = tabWrap.closest('.fx-exp');
      if (!section) return;
      var tabs = qsa('[data-exp-tab]', tabWrap);
      var panels = qsa('[data-exp-panel]', section);

      function setActive(id) {
        tabs.forEach(function (t) {
          var active = t.getAttribute('data-exp-tab') === id;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-exp-panel') === id);
        });
      }

      tabs.forEach(function (tab) {
        on(tab, 'click', function () { setActive(tab.getAttribute('data-exp-tab')); });
      });
    });
  }

  /* ------------------------------------------------------------------
     Wishlist toggle (localStorage)
  ------------------------------------------------------------------ */
  function initWishlist() {
    var STORAGE_KEY = 'fitlix_wishlist';

    function getWishlist() {
      try {
        var raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        // Skip legacy bare-ID entries (pre-handle storage format) — self-healing,
        // they simply won't appear on the wishlist listing page.
        return raw.filter(function (item) { return item && typeof item === 'object' && item.id; });
      } catch (e) {
        return [];
      }
    }
    function saveWishlist(list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) { /* noop */ }
    }
    function findIndex(list, id) {
      for (var i = 0; i < list.length; i++) { if (list[i].id === id) return i; }
      return -1;
    }

    var wishlist = getWishlist();

    function bindWishlist(root) {
    qsa('[data-wishlist-toggle]', root).forEach(function (btn) {
      if (btn.hasAttribute('data-fx-bound')) return;
      btn.setAttribute('data-fx-bound', '');
      wishlist = getWishlist();
      var id = btn.getAttribute('data-product-id');
      var handle = btn.getAttribute('data-product-handle');
      if (findIndex(wishlist, id) !== -1) btn.setAttribute('aria-pressed', 'true');

      on(btn, 'click', function () {
        var idx = findIndex(wishlist, id);
        if (idx === -1) {
          wishlist.push({ id: id, handle: handle });
          btn.setAttribute('aria-pressed', 'true');
          // Pop only fires on "like" — a removal shouldn't get a celebratory animation.
          btn.classList.remove('is-popping');
          // eslint-disable-next-line no-unused-expressions
          void btn.offsetWidth; // restart the animation if clicked again mid-transition
          btn.classList.add('is-popping');
        } else {
          wishlist.splice(idx, 1);
          btn.setAttribute('aria-pressed', 'false');
        }
        saveWishlist(wishlist);
      });
      on(btn, 'animationend', function () { btn.classList.remove('is-popping'); });
    });
    }
    bindWishlist(document);
    cardBinders.push(bindWishlist);
  }

  /* ------------------------------------------------------------------
     Reveal-on-scroll (.fx-animate)
  ------------------------------------------------------------------ */
  function initRevealOnScroll() {
    // Stagger groups: containers marked [data-stagger] (product/journal grids,
    // feature-card rows) reveal as one unit — the group is what's observed,
    // and its .fx-animate children each get an incremental --stagger-delay
    // (capped via --stagger-max) so they cascade in together rather than
    // popping in independently as each one individually crosses the viewport.
    var staggerChildren = [];
    qsa('[data-stagger]').forEach(function (group) {
      var children = Array.prototype.filter.call(group.children, function (el) {
        return el.classList.contains('fx-animate');
      });
      var step = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--stagger-step')) || 60;
      var max = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--stagger-max')) || 360;
      children.forEach(function (el, i) {
        el.style.setProperty('--stagger-delay', Math.min(i * step, max) + 'ms');
        el.setAttribute('data-stagger-child', '');
      });
      staggerChildren = staggerChildren.concat(children);
    });

    var items = qsa('.fx-animate').filter(function (el) { return !el.hasAttribute('data-stagger-child'); });
    var groups = qsa('[data-stagger]');

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      staggerChildren.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    // Elements already on screen when this runs must appear immediately —
    // waiting on IntersectionObserver's async first callback (which can lag
    // behind first paint on content-heavy pages) is what caused product
    // grids to render blank until the user scrolled.
    function isNearInitialViewport(el) {
      var rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

    items.forEach(function (el) {
      if (isNearInitialViewport(el)) {
        el.classList.add('is-visible');
      } else {
        io.observe(el);
      }
    });

    var groupIo = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          Array.prototype.filter.call(entry.target.children, function (el) {
            return el.classList.contains('fx-animate');
          }).forEach(function (el) { el.classList.add('is-visible'); });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

    groups.forEach(function (el) {
      if (isNearInitialViewport(el)) {
        Array.prototype.filter.call(el.children, function (c) { return c.classList.contains('fx-animate'); }).forEach(function (c) { c.classList.add('is-visible'); });
      } else {
        groupIo.observe(el);
      }
    });
  }

  /* ------------------------------------------------------------------
     Stat count-up animation
  ------------------------------------------------------------------ */
  function initCountUp() {
    var stats = qsa('[data-count-up]');
    if (!stats.length) return;

    function animateValue(el, target) {
      var duration = 1400;
      var start = null;
      function step(timestamp) {
        if (!start) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var current = Math.floor(progress * target);
        el.textContent = current;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.textContent = target;
        }
      }
      window.requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var raw = el.getAttribute('data-value') || '';
        var numeric = raw.replace(/[^0-9]/g, '');
        if (numeric && /^[0-9]+$/.test(numeric) && numeric === raw.trim()) {
          animateValue(el, parseInt(numeric, 10));
        } else {
          el.classList.add('is-visible');
          el.style.opacity = '0';
          el.style.transition = 'opacity .8s ease';
          requestAnimationFrame(function () { el.style.opacity = '1'; });
        }
        observer.unobserve(el);
      });
    }, { threshold: 0.4 });

    stats.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     Testimonial slider prev/next
  ------------------------------------------------------------------ */
  function initTestimonialSlider() {
    qsa('[data-testi-track]').forEach(function (track) {
      var section = track.closest('section');
      if (!section) return;
      var prevBtn = qs('[data-testi-prev]', section);
      var nextBtn = qs('[data-testi-next]', section);
      var card = qs('.fx-testi__card', track);
      var scrollAmount = card ? card.offsetWidth + 20 : 380;

      on(prevBtn, 'click', function () {
        track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
      on(nextBtn, 'click', function () {
        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    });
  }

  /* ------------------------------------------------------------------
     Newsletter popup — sessionStorage gate + delay timer
  ------------------------------------------------------------------ */
  function initNewsletterPopup() {
    var popup = qs('[data-newsletter-popup]');
    if (!popup) return;

    var SESSION_KEY = 'fitlix_newsletter_shown';
    var DISMISS_KEY = 'fitlix_newsletter_dismissed_at';
    var SUBSCRIBED_KEY = 'fitlix_newsletter_subscribed';
    var DISMISS_DAYS = 7;

    // The customer form reloads the page. Shopify then renders the success
    // (code) or error state inside this still-hidden popup, so it has to open
    // straight away or the shopper never sees their code.
    var posted = qs('.fx-newsletter-popup__success', popup) ? 'success' : (qs('.fx-newsletter-popup__error', popup) ? 'error' : '');
    if (posted === 'success') {
      try { localStorage.setItem(SUBSCRIBED_KEY, String(Date.now())); } catch (e) { /* noop */ }
      document.dispatchEvent(new CustomEvent('fx:offer-unlocked'));
    }

    var autoShow = !posted;
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY)) autoShow = false;      // already signed up: never ask again
      if (sessionStorage.getItem(SESSION_KEY)) autoShow = false;      // once per visit
    } catch (e) { /* if storage unavailable, still allow popup */ }

    var delay = parseInt(popup.getAttribute('data-delay'), 10) || 8;

    var lastFocused = null;
    var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function focusable() {
      return Array.prototype.filter.call(
        popup.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null && !el.disabled; }
      );
    }

    // Keep Tab inside the dialog. Without this the keyboard walks straight out
    // into the page behind an aria-modal panel, which is the single most common
    // modal accessibility bug.
    function onTrap(e) {
      if (e.key !== 'Tab') return;
      var items = focusable();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    // Never interrupt a purchase: skip when the cart drawer or another dialog is open,
    // when the shopper already has items in the cart, or while they are typing.
    function shouldSuppress() {
      var drawer = qs('#fx-cart-drawer');
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') return true;
      var active = document.activeElement;
      if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) && !popup.contains(active)) return true;
      // A closed drawer keeps aria-modal on its panel and stays in layout
      // (it slides off-screen, it isn't display:none), so "has an offsetParent"
      // counted the hidden cart drawer as open and the popup never showed at
      // all. Count a dialog only when nothing above it is aria-hidden.
      var dialogs = qsa('[aria-modal="true"]');
      for (var i = 0; i < dialogs.length; i++) {
        var d = dialogs[i];
        if (popup.contains(d) || d.closest('[aria-hidden="true"]')) continue;
        if (d.getClientRects().length) return true;
      }
      return false;
    }

    function open(force) {
      if (popup.getAttribute('aria-hidden') === 'false') return;
      if (!force && shouldSuppress()) return;
      lastFocused = document.activeElement;
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* noop */ }
      document.addEventListener('keydown', onTrap);
      var email = popup.querySelector('input[type="email"]');
      var target = email || popup.querySelector('[data-newsletter-close]');
      if (target) {
        // Focus the field, but do not scroll the page behind the backdrop.
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    }
    function close() {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* noop */ }
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onTrap);
      // Send focus back where it came from, so closing does not dump the user
      // at the top of the document.
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (e) { /* noop */ }
      }
    }

    // "Unlock offer" on a product page asks for the popup directly. Once the
    // shopper has signed up, let the product page just show the code instead.
    on(document, 'fx:offer-unlock', function (e) {
      var subscribed = false;
      try { subscribed = !!localStorage.getItem(SUBSCRIBED_KEY); } catch (err) { /* noop */ }
      if (subscribed) return;
      e.preventDefault();
      clearTimeout(timer);
      open(true);
    });

    var timer = null;
    if (posted) {
      open(true);
    } else if (autoShow) {
      var armedAt = Date.now();
      var onExitIntent = function (e) {
        if (e.clientY > 40 || Date.now() - armedAt < 5000) return;
        clearTimeout(timer);
        document.removeEventListener('mouseout', onExitIntent);
        open();
      };
      timer = setTimeout(function () {
        document.removeEventListener('mouseout', onExitIntent);
        open();
      }, delay * 1000);
      document.addEventListener('mouseout', onExitIntent);
    }

    qsa('[data-newsletter-close]', popup).forEach(function (btn) { on(btn, 'click', close); });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') close();
    });
  }

  /* ------------------------------------------------------------------
     Hero slider (multi-slide banner)
  ------------------------------------------------------------------ */
  function initHeroSlider() {
    qsa('[data-hero-slider]').forEach(function (wrap) {
      var track = qs('[data-hero-track]', wrap);
      var slides = qsa('.fx-hero-slide', track);
      if (!track || slides.length < 2) return;

      var dots = qsa('[data-hero-dot]', wrap);
      var prevBtn = qs('[data-hero-prev]', wrap);
      var nextBtn = qs('[data-hero-next]', wrap);
      var current = 0;
      var timer = null;
      var speed = parseInt(wrap.dataset.speed, 10) || 6000;

      // On a phone, a sideways swipe means the visitor is choosing what to look
      // at, so autoplay stays off for the rest of the visit (a plain touch or a
      // page scroll does not count). On a computer the banner fills most of
      // the screen and the mouse is nearly always over it, so hovering must
      // NOT pause it (that is why it looked stuck on desktop); an arrow, dot,
      // key or mouse drag pauses it briefly, then it carries on by itself.
      var userTookOver = false;
      var resumeTimer = null;
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Off-screen posters are loading="lazy", and inside an overflow:hidden
      // track the browser never sees them as "near the viewport", so they would
      // only start downloading once already on screen - a blank frame mid-slide.
      // Switch the neighbours to eager just ahead of time instead.
      function warm(i) {
        var s = slides[(i + slides.length) % slides.length];
        if (!s) return;
        qsa('img[loading="lazy"]', s).forEach(function (img) { img.loading = 'eager'; });
      }
      function goTo(index) {
        current = (index + slides.length) % slides.length;
        warm(current + 1);
        warm(current - 1);
        track.style.transform = 'translateX(-' + (current * 100) + '%)';
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === current); });
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === current); });
      }
      function next() { goTo(current + 1); }

      function startAutoplay() {
        clearInterval(timer);
        if (userTookOver || reduceMotion) return;
        timer = setInterval(next, speed);
      }
      function stopAutoplay() { clearInterval(timer); timer = null; }
      function takeOver() { userTookOver = true; clearTimeout(resumeTimer); stopAutoplay(); }
      // pause, then resume on its own after two slides' worth of quiet
      function pauseBriefly() {
        stopAutoplay();
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(startAutoplay, speed * 2);
      }

      on(prevBtn, 'click', function () { pauseBriefly(); goTo(current - 1); });
      on(nextBtn, 'click', function () { pauseBriefly(); goTo(current + 1); });
      dots.forEach(function (dot, i) {
        on(dot, 'click', function () { pauseBriefly(); goTo(i); });
      });

      // Keyboard users: pause while focus is inside, as before. A mouse click
      // also focuses the link/button, so only count keyboard focus.
      on(wrap, 'focusin', function (e) {
        var fv = false; try { fv = e.target.matches(':focus-visible'); } catch (x) {}
        if (fv) stopAutoplay();
      });
      on(wrap, 'focusout', function (e) { if (!wrap.contains(e.relatedTarget)) startAutoplay(); });

      on(wrap, 'keydown', function (e) {
        if (e.key === 'ArrowLeft') { pauseBriefly(); goTo(current - 1); }
        else if (e.key === 'ArrowRight') { pauseBriefly(); goTo(current + 1); }
      });

      // ---- swipe (phone) and drag (mouse), one code path via pointer events --
      // The track follows the finger, then snaps to the next/previous slide if
      // the move passed a threshold, otherwise back. touch-action:pan-y in the
      // CSS hands vertical moves to the browser so page scrolling still works;
      // the browser then fires pointercancel, which just snaps back.
      var startX = 0, startY = 0, dx = 0, pointerId = null, pointerType = '', dragging = false, suppressClick = false;
      var THRESHOLD = 8;

      function setOffset(px) {
        track.style.transform = 'translateX(calc(-' + (current * 100) + '% + ' + px + 'px))';
      }

      on(track, 'pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // A finger landing on the banner is not a decision - people rest a
        // thumb here while scrolling the page. Hold still while it is down so
        // the poster under the finger does not change, and resume on release.
        // Only an actual sideways swipe (below) stops autoplay for good.
        stopAutoplay();
        warm(current + 1);
        warm(current - 1);
        pointerId = e.pointerId;
        pointerType = e.pointerType;
        startX = e.clientX; startY = e.clientY; dx = 0;
        dragging = false;
        suppressClick = false;
      });

      on(track, 'pointermove', function (e) {
        if (e.pointerId !== pointerId) return;
        var mx = e.clientX - startX;
        var my = e.clientY - startY;
        if (!dragging) {
          if (Math.abs(mx) < THRESHOLD || Math.abs(mx) < Math.abs(my)) return;
          dragging = true;
          // a finger swipe decides for the rest of the visit; a mouse drag
          // only pauses for a moment
          if (pointerType === 'mouse') pauseBriefly(); else takeOver();
          // Capture only once it is a real drag. Capturing on pointerdown would
          // retarget the click to the track and break every link and button.
          try { track.setPointerCapture(pointerId); } catch (err) {}
          track.classList.add('is-dragging');
        }
        dx = mx;
        // Resist past the ends a little instead of letting the track fly off.
        var w = wrap.offsetWidth || 1;
        if (Math.abs(dx) > w) dx = (dx > 0 ? 1 : -1) * w;
        setOffset(dx);
      });

      function endDrag(cancelled) {
        if (pointerId === null) return;
        pointerId = null;
        if (!dragging) {
          // Touch, click or page-scroll that never went sideways: keep rotating.
          startAutoplay();
          return;
        }
        dragging = false;
        track.classList.remove('is-dragging');
        var w = wrap.offsetWidth || 1;
        var limit = Math.min(80, w * 0.18);
        if (!cancelled && dx <= -limit) goTo(current + 1);
        else if (!cancelled && dx >= limit) goTo(current - 1);
        else goTo(current);
        suppressClick = Math.abs(dx) > THRESHOLD;
      }
      on(track, 'pointerup', function () { endDrag(false); });
      on(track, 'pointercancel', function () { endDrag(true); });
      // Touch pointers are implicitly captured by whatever the finger landed on
      // (usually the image). Taking capture onto the track releases that one,
      // and its lostpointercapture bubbles up here - only the track's own
      // capture ending means the drag is over.
      on(track, 'lostpointercapture', function (e) { if (e.target === track) endDrag(false); });

      // A drag that ends over a link must not also open it.
      track.addEventListener('click', function (e) {
        if (suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick = false;
        }
      }, true);
      // Browsers start a native "drag the image" on mousedown otherwise.
      on(track, 'dragstart', function (e) { e.preventDefault(); });

      slides.forEach(function (slide) {
        var link = qs('.fx-hero__actions a, .fx-hero-poster__link', slide);
        if (!link) return;
        on(link, 'click', function () {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'ad_click', { ad_category: slide.dataset.category || 'brand' });
          }
        });
      });

      // first poster is eager; fetch its neighbours once the page has loaded
      if (document.readyState === 'complete') { warm(1); warm(-1); }
      else window.addEventListener('load', function () { warm(1); warm(-1); });

      startAutoplay();
    });
  }

  /* ------------------------------------------------------------------
     Journal "Popular Articles" slider
  ------------------------------------------------------------------ */
  function initJournalSlider() {
    qsa('[data-journal-slider]').forEach(function (wrap) {
      var track = qs('[data-journal-track]', wrap);
      var slides = qsa('.fx-journal-popular__slide', track);
      if (!track || slides.length < 2) return;

      var prevBtn = qs('[data-journal-prev]', wrap.parentElement);
      var nextBtn = qs('[data-journal-next]', wrap.parentElement);
      var current = 0;

      function goTo(index) {
        current = (index + slides.length) % slides.length;
        track.style.transform = 'translateX(-' + (current * 100) + '%)';
      }

      on(prevBtn, 'click', function () { goTo(current - 1); });
      on(nextBtn, 'click', function () { goTo(current + 1); });
    });
  }

  /* ------------------------------------------------------------------
     Facets - collection and search

     Shopify's storefront filter parameters (filter.p.vendor,
     filter.p.product_type, filter.v.option.*) are ignored on this store because
     Search & Discovery is not configured; each was tested and returned the
     collection unfiltered. Liquid also cannot read an arbitrary query string.
     So brand, category, size and flavour are matched here, over the grid that
     Shopify already rendered. Every listing on this store fits a single page,
     so the result is exact.

     Price is the exception and stays on Shopify's own parameter, because that
     one works server side.

     Selections are mirrored into the URL, so a filtered view can be shared,
     reloaded and reached with the back button.
  ------------------------------------------------------------------ */
  function initCollectionFilters() {
    var facets = qs('[data-facets]');

    if (facets) {
      var panel   = qs('[data-facets-panel]', facets);
      var openBtn = qs('[data-facets-open]');
      var chipsEl = qs('[data-facets-chips]', facets);
      var badge   = qs('[data-facets-badge]', facets);
      var countEl = qs('[data-facets-result-count]', facets);
      var grid    = qs('.fx-collection__grid') || qs('.fx-search-results__grid');
      var cards   = grid ? qsa('[data-product-card]', grid) : [];
      var lastFocus = null;
      // Infinite scroll appends the next page's cards: include them, and hide
      // any that the active filters exclude.
      on(document, 'fx:cards-added', function () {
        cards = grid ? qsa('[data-product-card]', grid) : [];
        apply(false);
      });

      var KEYS = ['brand', 'ptype', 'sizes', 'flavours'];
      var LABEL = { brand: 'Brand', ptype: 'Category', sizes: 'Size', flavours: 'Flavour' };
      var state = { brand: [], ptype: [], sizes: [], flavours: [] };

      /* ---- a card's values for one facet ---- */
      function cardValues(card, key) {
        if (key === 'brand')  return [card.getAttribute('data-brand') || ''];
        if (key === 'ptype')  return [card.getAttribute('data-ptype') || ''];
        var raw = card.getAttribute(key === 'sizes' ? 'data-sizes' : 'data-flavours') || '';
        return raw.split('|').filter(Boolean);
      }

      // Selections inside one group are OR, across groups AND. That is what
      // shoppers expect: two brands widens, brand plus size narrows.
      function matches(card, skipKey) {
        for (var i = 0; i < KEYS.length; i++) {
          var k = KEYS[i];
          if (k === skipKey || !state[k].length) continue;
          var vals = cardValues(card, k);
          var hit = state[k].some(function (want) { return vals.indexOf(want) !== -1; });
          if (!hit) return false;
        }
        return true;
      }

      function apply(pushUrl) {
        var visible = 0;
        cards.forEach(function (c) {
          var ok = matches(c, null);
          c.hidden = !ok;
          if (ok) visible++;
        });

        // Counts show what each option would leave, ignoring its own group, so
        // a number never contradicts what tapping it does.
        qsa('[data-count-for]', panel).forEach(function (el) {
          var parts = el.getAttribute('data-count-for').split('::');
          var key = parts[0], val = parts.slice(1).join('::');
          var n = 0;
          cards.forEach(function (c) {
            if (cardValues(c, key).indexOf(val) !== -1 && matches(c, key)) n++;
          });
          el.textContent = n;
          var li = el.closest('.fx-facets__item');
          var box = li && qs('.fx-facets__input', li);
          if (li && box) li.classList.toggle('is-nil', n === 0 && !box.checked);
        });

        var total = 0;
        KEYS.forEach(function (k) { total += state[k].length; });
        if (badge) { badge.hidden = total === 0; badge.textContent = total; }
        // "Top picks" / "More" headings describe the unfiltered ranking; once a
        // filter is on they would label the wrong cards, so they step aside.
        qsa('[data-rank-label]').forEach(function (l) { l.hidden = total > 0; });
        if (countEl) countEl.textContent = visible;
        KEYS.forEach(function (k) {
          var c = qs('[data-group-count="' + k + '"]', panel);
          if (c) { c.hidden = state[k].length === 0; c.textContent = state[k].length; }
        });

        renderChips();
        emptyState(visible);
        if (pushUrl !== false) syncUrl();
      }

      function renderChips() {
        if (!chipsEl) return;
        chipsEl.innerHTML = '';
        var any = false;
        KEYS.forEach(function (k) {
          state[k].forEach(function (v) {
            any = true;
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'fx-facets__chip';
            b.setAttribute('aria-label', 'Remove filter ' + LABEL[k] + ' ' + v);
            var s = document.createElement('span');
            s.textContent = v;
            b.appendChild(s);
            var x = document.createElement('span');
            x.className = 'fx-facets__chipx';
            x.setAttribute('aria-hidden', 'true');
            b.appendChild(x);
            on(b, 'click', function () { toggle(k, v, false); });
            chipsEl.appendChild(b);
          });
        });
        chipsEl.hidden = !any;
      }

      function emptyState(visible) {
        var msg = qs('[data-facets-empty]');
        if (!msg || !grid) return;
        msg.hidden = visible !== 0;
      }

      function toggle(key, val, on_) {
        var i = state[key].indexOf(val);
        if (on_ && i === -1) state[key].push(val);
        if (!on_ && i !== -1) state[key].splice(i, 1);
        var box = panel.querySelector('[data-facet="' + key + '"][value="' + cssEscape(val) + '"]');
        if (box) box.checked = on_;
        apply();
      }

      function cssEscape(v) {
        return String(v).replace(/(["\\])/g, '\\$1');
      }

      /* ---- URL, so a filtered view survives share, reload and back ---- */
      function syncUrl() {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        KEYS.forEach(function (k) {
          url.searchParams.delete('fx_' + k);
          if (state[k].length) url.searchParams.set('fx_' + k, state[k].join('~'));
        });
        window.history.replaceState({}, '', url.toString());
      }
      function readUrl() {
        var url = new URL(window.location.href);
        KEYS.forEach(function (k) {
          var raw = url.searchParams.get('fx_' + k);
          if (!raw) return;
          state[k] = raw.split('~').filter(Boolean);
          state[k].forEach(function (v) {
            var box = panel.querySelector('[data-facet="' + k + '"][value="' + cssEscape(v) + '"]');
            if (box) box.checked = true;
          });
        });
      }

      qsa('[data-facet]', panel).forEach(function (box) {
        on(box, 'change', function () {
          toggle(box.getAttribute('data-facet'), box.value, box.checked);
        });
      });

      on(qs('[data-facets-clear]', panel), 'click', function () {
        KEYS.forEach(function (k) { state[k] = []; });
        qsa('[data-facet]', panel).forEach(function (b) { b.checked = false; });
        apply();
      });

      /* ---- drawer ---- */
      function open() {
        lastFocus = document.activeElement;
        facets.setAttribute('aria-hidden', 'false');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onTrap);
        var f = qs('[data-facets-close]', panel);
        if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
      }
      function close() {
        facets.setAttribute('aria-hidden', 'true');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onTrap);
        if (lastFocus && lastFocus.focus) {
          try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
      }
      var FOCUSABLE = 'a[href], button, input, select, summary, [tabindex]:not([tabindex="-1"])';
      function onTrap(e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        var f = Array.prototype.filter.call(
          panel.querySelectorAll(FOCUSABLE),
          function (n) { return n.offsetParent !== null && !n.disabled; }
        );
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
      on(openBtn, 'click', open);
      qsa('[data-facets-close]', facets).forEach(function (b) { on(b, 'click', close); });

      /* ---- price, the one facet Shopify still handles server side ---- */
      var priceForm = qs('[data-price-form]', panel);
      if (priceForm) {
        var minI = qs('[data-price-min]', priceForm);
        var maxI = qs('[data-price-max]', priceForm);
        var hint = qs('[data-price-hint]', priceForm);
        function priceOk() {
          var a = parseFloat(minI.value), b = parseFloat(maxI.value);
          var bad = !isNaN(a) && !isNaN(b) && a > b;
          if (hint) hint.hidden = !bad;
          minI.setAttribute('aria-invalid', bad ? 'true' : 'false');
          return !bad;
        }
        [minI, maxI].forEach(function (i) { if (i) on(i, 'input', priceOk); });
        on(priceForm, 'submit', function (e) {
          if (!priceOk()) { e.preventDefault(); return; }
          qsa('input[type="number"]', priceForm).forEach(function (i) {
            if (!i.value) i.disabled = true;
          });
        });
      }

      /* ---- search inside a long group ---- */
      qsa('[data-facet-search]', panel).forEach(function (field) {
        var body = field.closest('.fx-facets__groupbody');
        var items = qsa('.fx-facets__item', body);
        var none = qs('[data-facet-empty]', body);
        on(field, 'input', function () {
          var q = field.value.trim().toLowerCase();
          var hits = 0;
          items.forEach(function (li) {
            var ok = !q || (li.getAttribute('data-facet-value') || '').indexOf(q) !== -1;
            li.hidden = !ok;
            if (ok) hits++;
          });
          if (none) none.hidden = hits !== 0;
        });
      });

      readUrl();
      apply(false);
    }

    qsa('[data-sort-select]').forEach(function (select) {
      on(select, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    });
  }

  /* ------------------------------------------------------------------
     Journal category filtering (AJAX, section-rendering API)
     Same technique fitlix-related-products.liquid already uses for its
     own lazy-load: fetch ?section_id=X, pull the matching id out of the
     response, splice its markup into the live page.
  ------------------------------------------------------------------ */
  function initJournalFilter() {
    var section = qs('[data-journal-latest]');
    var contentEl = section && qs('[data-journal-latest-content]', section);
    var sectionId = section && section.getAttribute('data-section-id');
    if (!section || !contentEl || !sectionId) return;

    var requestSeq = 0;

    function revealFreshContent(container) {
      qsa('.fx-animate', container).forEach(function (el) { el.classList.add('is-visible'); });
      qsa('img', container).forEach(fadeInImage);
    }

    function tagFromUrl(url) {
      var match = url.match(/\/tagged\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    function setActiveCategoryButtons(url) {
      var tag = tagFromUrl(url);
      qsa('.fx-journal-category-btn').forEach(function (btn) {
        var href = btn.getAttribute('href') || '';
        btn.classList.toggle('is-active', !!tag && tagFromUrl(href) === tag);
      });
    }

    function loadUrl(url, pushHistory) {
      var seq = ++requestSeq;
      contentEl.classList.add('is-loading');
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + sep + 'section_id=' + sectionId)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          if (seq !== requestSeq) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var freshSection = tmp.querySelector('#latest-articles');
          var freshContent = freshSection && qs('[data-journal-latest-content]', freshSection);
          if (freshContent) {
            contentEl.innerHTML = freshContent.innerHTML;
            revealFreshContent(contentEl);
          }
          contentEl.classList.remove('is-loading');
          setActiveCategoryButtons(url);
          if (pushHistory) window.history.pushState({ fxJournal: true, url: url }, '', url);
          contentEl.focus({ preventScroll: true });
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function () {
          if (seq !== requestSeq) return;
          window.location.href = url;
        });
    }

    on(document, 'click', function (e) {
      // Only take over plain left-clicks — leave modifier/middle-clicks (open
      // in new tab, etc.) to the browser's native <a> handling.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest('.fx-journal-category-btn, .fx-journal-latest__clear-filter');
      if (!link) return;
      e.preventDefault();
      loadUrl(link.getAttribute('href'), true);
    });

    on(window, 'popstate', function () {
      // location.href is authoritative here, not e.state — navigating back
      // past the very first AJAX filter lands on the original entry, which
      // was never pushed by us and so carries no fxJournal state at all.
      loadUrl(window.location.href, false);
    });

    setActiveCategoryButtons(window.location.href);
  }

  /* ------------------------------------------------------------------
     Cart drawer free-gift / free-shipping progress bar
  ------------------------------------------------------------------ */
  function updateCartProgress(cart) {
    updateRewardsProgress(cart);
    var bar = qs('[data-cart-progress-bar]');
    var label = qs('[data-cart-progress-label]');
    if (!bar || !cart) return;

    var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    if (!threshold) return;

    var total = cart.total_price || 0;
    var pct = Math.min((total / threshold) * 100, 100);
    bar.style.width = pct + '%';

    if (label) {
      if (total >= threshold) {
        label.textContent = label.getAttribute('data-reached-text') || 'You unlocked free shipping!';
      } else {
        var remaining = ((threshold - total) / 100).toFixed(0);
        var template = label.getAttribute('data-progress-text') || 'Add ₹{amount} more to unlock free shipping';
        label.textContent = template.replace('{amount}', remaining);
      }
    }
  }

  // Welcome-offer bar in the drawer. Its two messages are rendered by Liquid
  // (they depend on the automatic/code setting); only the amount is filled here.
  function updateRewardsProgress(cart) {
    var label = qs('[data-cart-rewards-label]');
    var bar = qs('[data-cart-rewards-bar]');
    if (!label || !cart) return;
    var threshold = parseInt(label.getAttribute('data-threshold'), 10) || 0;
    if (!threshold) return;
    var total = cart.total_price || 0;
    if (bar) bar.style.width = Math.min((total / threshold) * 100, 100) + '%';
    if (total >= threshold) {
      label.innerHTML = label.getAttribute('data-reached-html') || '';
    } else {
      var amount = '₹' + Math.ceil((threshold - total) / 100).toLocaleString('en-IN');
      label.innerHTML = (label.getAttribute('data-progress-html') || '').replace('[amount]', amount);
    }
  }

  /* ------------------------------------------------------------------
     Shopper profile + "Picked for you"
     Everything stays in this browser (localStorage). Signals:
       fitlix_recently_viewed  written on product pages (with category)
       fitlix_interest         category pages visited  {handle: {n, t, title}}
       fitlix_goal             last "Shop by goal" pick {title, handle, t}
     Entries older than 30 days are ignored.
  ------------------------------------------------------------------ */
  var PROFILE_TTL = 30 * 24 * 3600 * 1000;
  function lsGet(k, fallback) {
    try { var v = JSON.parse(localStorage.getItem(k)); return v == null ? fallback : v; } catch (e) { return fallback; }
  }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }

  function initProfile() {
    var banner = qs('[data-interest-collection]');
    if (banner) {
      var h = banner.getAttribute('data-interest-collection');
      var interest = lsGet('fitlix_interest', {});
      var rec = interest[h] || { n: 0 };
      rec.n += 1;
      rec.t = Date.now();
      rec.title = banner.getAttribute('data-interest-title') || h;
      interest[h] = rec;
      lsSet('fitlix_interest', interest);
    }
    on(document, 'click', function (e) {
      var g = e.target.closest && e.target.closest('[data-goal-title]');
      if (!g || !e.target.closest('a')) return;
      lsSet('fitlix_goal', {
        title: g.getAttribute('data-goal-title'),
        handle: g.getAttribute('data-goal-collection'),
        t: Date.now()
      });
    });
  }

  function readProfile() {
    var now = Date.now();
    var goal = lsGet('fitlix_goal', null);
    if (goal && (!goal.handle || now - goal.t > PROFILE_TTL)) goal = null;

    // Category score: a product looked at counts 3, a category page visit 1.
    var score = {}, titles = {};
    var interest = lsGet('fitlix_interest', {});
    Object.keys(interest).forEach(function (h) {
      var r = interest[h];
      if (!r || now - (r.t || 0) > PROFILE_TTL) return;
      score[h] = (score[h] || 0) + (r.n || 0);
      titles[h] = r.title || h;
    });
    var viewed = lsGet('fitlix_recently_viewed', []);
    var prices = [];
    viewed.forEach(function (v) {
      if (v.category) {
        score[v.category] = (score[v.category] || 0) + 3;
        if (v.categoryTitle) titles[v.category] = v.categoryTitle;
      }
      if (v.price) prices.push(v.price);
    });
    var top = null;
    Object.keys(score).forEach(function (h) { if (!top || score[h] > score[top]) top = h; });

    prices.sort(function (a, b) { return a - b; });
    return {
      goal: goal,
      top: top ? { handle: top, title: titles[top] || top } : null,
      seen: viewed.map(function (v) { return String(v.id); }),
      budget: prices.length ? prices[Math.floor(prices.length / 2)] : 0
    };
  }

  function initForYou() {
    var root = qs('[data-for-you]');
    var row = root && qs('[data-for-you-row]', root);
    if (!row) return;

    var p = readProfile();
    var pick = null;
    if (p.goal) {
      pick = { handle: p.goal.handle, eyebrow: 'For your goal',
               heading: 'Best match for ' + p.goal.title,
               sub: 'Our top-ranked picks for the goal you chose.' };
    } else if (p.top) {
      pick = { handle: p.top.handle, eyebrow: 'Picked for you',
               heading: 'More ' + p.top.title + ' you might like',
               sub: "Based on what you've been looking at." };
    }
    if (!pick) return;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = base + 'collections/' + encodeURIComponent(pick.handle);
    fetch(url + '?section_id=fx-reco-strip')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var items = qsa('[data-reco-item]', tmp);
        if (items.length < 2) return;

        // Keep the ranking, but move already-seen products and ones far above
        // this visitor's usual spend to the back.
        var cap = p.budget ? p.budget * 2 : Infinity;
        function weight(el) {
          var w = 0;
          if (p.seen.indexOf(el.getAttribute('data-id')) !== -1) w += 2;
          if (Number(el.getAttribute('data-price')) > cap) w += 1;
          return w;
        }
        var ordered = items.map(function (el, i) { return { el: el, w: weight(el), i: i }; })
          .sort(function (a, b) { return a.w - b.w || a.i - b.i; })
          .slice(0, 8)
          .map(function (x) { return x.el; });

        var grid = qs('[data-for-you-grid]', row);
        grid.innerHTML = '';
        ordered.forEach(function (el) { grid.appendChild(el); });
        qsa('.fx-animate', grid).forEach(function (el) { el.classList.add('is-visible'); });
        document.dispatchEvent(new CustomEvent('fx:cards-added', { detail: { root: grid } }));
        qs('[data-for-you-eyebrow]', row).textContent = pick.eyebrow;
        qs('[data-for-you-heading]', row).textContent = pick.heading;
        qs('[data-for-you-sub]', row).textContent = pick.sub;
        qs('[data-for-you-all]', row).setAttribute('href', url);
        row.hidden = false;
      })
      .catch(function () { /* the row just stays hidden */ });

    on(qs('[data-for-you-reset]', row), 'click', function () {
      try {
        localStorage.removeItem('fitlix_interest');
        localStorage.removeItem('fitlix_goal');
        localStorage.removeItem('fitlix_recently_viewed');
      } catch (e) { /* noop */ }
      row.hidden = true;
    });
  }

  /* ------------------------------------------------------------------
     Init on DOM ready
  ------------------------------------------------------------------ */
  function init() {
    initHeader();
    initMobileDrawer();
    initSearchDrawer();
    initMegaMenu();
    initCart();
    initProductTabs();
    initExperienceTabs();
    initWishlist();
    initRevealOnScroll();
    initCountUp();
    initTestimonialSlider();
    initNewsletterPopup();
    initCollectionFilters();
    initHeroSlider();
    initProfile();
    initForYou();
    initImageFade();
    initJournalFilter();

    // fx:cards-added { detail: { root } } - fired by fitlix-infinite.js
    on(document, 'fx:cards-added', function (e) { if (e.detail && e.detail.root) bindCards(e.detail.root); });

    on(document, 'cart:updated', function (e) {
      updateCartProgress(e.detail && e.detail.cart);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
